import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Divider, Empty, Modal, notification, Grid } from 'antd';
import { Main, BasicFormWrapper } from '../../../styled';
import {
  fetchTaskGroups,
  fetchTaskVerbiages,
  completeTaskVerbiage,
  completeTaskVerbiageBulk,
} from '../../../../redux/manageTasks/actionCreator';
import { fetchHostListing } from '../../../../redux/manageSettings/actionCreator';
import HttpStatus from '../../../../utility/status.enum';
import Spinner from '../../../common/Spinner';
import { Cards } from '../../../../components/cards/frame/cards-frame';
import { Button } from '../../../../components/buttons/buttons';
import Heading from '../../../../components/heading/heading';
import { alertModal } from '../../../../components/modals/antd-modals';
import { AddUser } from '../../style';

/* ─── SVG copy icon ──────────────────────────────────────────────────────── */
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ─── PMS block: title + instruction ────────────────────────────────────── */
const renderPmsBlock = (pms) => {
  if (!pms) return null;
  return (
    <div className="task-pms-block">
      {pms.cms_title && (
        <strong className="scheduler-main-title color-dark">{pms.cms_title}</strong>
      )}
      {pms.cms_instruction && (
        <div
          className="task-pms-instruction pb-19"
          dangerouslySetInnerHTML={{ __html: pms.cms_instruction }}
        />
      )}
    </div>
  );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
function Tasks({ hostSlug }) {
  const dispatch = useDispatch();

  const [taskGroups, setTaskGroups] = useState([]);
  const [tabHeader, setTabHeader] = useState('');
  const [emptyState, setEmptyState] = useState('');
  const [verbiagesMap, setVerbiagesMap] = useState({});
  const [listingNamesMap, setListingNamesMap] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [spinnerLoader, setSpinnerLoader] = useState(false);
  const [removingRows, setRemovingRows] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);

  const screens = Grid.useBreakpoint();
  const initialCardBodyPadding = screens.xs ? '20px 16px 24px' : '24px 38px 38px';
  const initialCardBodyPadding2 = screens.xs ? '20px 16px 24px' : '0px 38px 38px';

  useEffect(() => {
    if (!hostSlug) return;
    loadAllData();
  }, [hostSlug]);

  /* ── Data loading ──────────────────────────────────────────────────────── */
  const loadAllData = async () => {
    setLoadingTasks(true);
    try {
      const [tasksRes, listingsRes] = await Promise.all([
        dispatch(fetchTaskGroups({ host_slug: hostSlug })),
        dispatch(fetchHostListing({ host_slug: hostSlug })),
      ]);
      const groups = tasksRes?.result?.groups ?? [];
      setTabHeader(tasksRes?.result?.tab_header ?? '');
      setEmptyState(tasksRes?.result?.empty_state ?? '');
      setTaskGroups(groups);

      if (Array.isArray(listingsRes?.result)) {
        const map = {};
        listingsRes.result.forEach((l) => {
          map[l.slug] = l.property_name || l.name || l.slug;
        });
        setListingNamesMap(map);
      }

      if (groups.length > 0) {
        await Promise.all(groups.map((g) => loadVerbiages(g.task_slug)));
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || err?.message || 'Something went wrong.' });
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadVerbiages = async (task_slug) => {
    try {
      const res = await dispatch(fetchTaskVerbiages({ host_slug: hostSlug, task_slug }));
      if (res?.result?.verbiages) {
        const namesFromVerbiages = {};
        const verbiages = res.result.verbiages.map((v) => {
          const normalized = (v.pending_listings || []).map((item) => {
            if (item && typeof item === 'object') {
              if (item.listing_slug) namesFromVerbiages[item.listing_slug] = item.listing_name || item.listing_slug;
              return item.listing_slug;
            }
            return item;
          });
          return { ...v, pending_listings: normalized };
        });
        if (Object.keys(namesFromVerbiages).length > 0) {
          setListingNamesMap((prev) => ({ ...prev, ...namesFromVerbiages }));
        }
        setVerbiagesMap((prev) => ({ ...prev, [task_slug]: verbiages }));
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || err?.message || 'Something went wrong.' });
    }
  };

  /* ── Copy ──────────────────────────────────────────────────────────────── */
  const handleCopy = async (rendered_content, cardKey) => {
    const plain = new DOMParser()
      .parseFromString(rendered_content || '', 'text/html')
      .body.innerText;
    try {
      await navigator.clipboard.writeText(plain);
    } catch {
      const el = document.createElement('textarea');
      el.value = plain;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedKey(cardKey);
    setTimeout(() => setCopiedKey(null), 1400);
  };

  /* ── Mark single done ──────────────────────────────────────────────────── */
  const confirmMarkDone = (task_slug, verbiage_type, listing_slug) => {
    alertModal.confirm({
      title: `Confirm that you have pasted this content into your PMS for the selected listing.`,
      className: 'confirm_modal delete_listing_modal',
      okText: "Yes, Mark Done",
      onOk() { handleMarkDone(task_slug, verbiage_type, listing_slug); },
      onCancel() { },
    });
  };

  const handleMarkDone = async (task_slug, verbiage_type, listing_slug) => {
    setSpinnerLoader(true);
    try {
      const res = await dispatch(
        completeTaskVerbiage({ host_slug: hostSlug, task_slug, verbiage_type, listing_slug }),
      );
      if (res?.status === HttpStatus.HTTP_OK) {
        animateRowOut(task_slug, verbiage_type, listing_slug);
      } else {
        notification.error({ message: res?.message || 'Something went wrong.' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || err?.message || 'Something went wrong.' });
    } finally {
      setSpinnerLoader(false);
    }
  };

  /* ── Mark all done ─────────────────────────────────────────────────────── */
  const confirmMarkAllDone = (task_slug, verbiage_type, pending_listings) => {
    alertModal.confirm({
      title: `Confirm that you have pasted this content into your PMS for all ${pending_listings.length} listing(s).`,
      className: 'confirm_modal delete_listing_modal',
      okText: "Yes, Mark All Done",
      onOk() { handleMarkAllDone(task_slug, verbiage_type, pending_listings); },
      onCancel() { },
    });
  };

  const handleMarkAllDone = async (task_slug, verbiage_type, pending_listings) => {
    setSpinnerLoader(true);
    try {
      const res = await dispatch(
        completeTaskVerbiageBulk({
          host_slug: hostSlug,
          task_slug,
          verbiage_type,
          listing_slugs: pending_listings,
        }),
      );
      if (res?.status === HttpStatus.HTTP_OK) {
        const newRemoving = {};
        pending_listings.forEach((slug) => {
          newRemoving[`${task_slug}-${verbiage_type}-${slug}`] = true;
        });
        setRemovingRows((prev) => ({ ...prev, ...newRemoving }));
        setTimeout(() => {
          applyCompletionResult(task_slug, verbiage_type, pending_listings);
        }, 320);
      } else {
        notification.error({ message: res?.message || 'Something went wrong.' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || err?.message || 'Something went wrong.' });
    } finally {
      setSpinnerLoader(false);
    }
  };

  /* ── Animation + state cleanup ─────────────────────────────────────────── */
  const animateRowOut = (task_slug, verbiage_type, listing_slug) => {
    const rowKey = `${task_slug}-${verbiage_type}-${listing_slug}`;
    setRemovingRows((prev) => ({ ...prev, [rowKey]: true }));
    setTimeout(() => {
      applyCompletionResult(task_slug, verbiage_type, [listing_slug]);
    }, 320);
  };

  const applyCompletionResult = (task_slug, verbiage_type, removedSlugs) => {
    setVerbiagesMap((prev) => {
      const verbs = (prev[task_slug] || []).map((v) =>
        v.type === verbiage_type
          ? { ...v, pending_listings: v.pending_listings.filter((s) => !removedSlugs.includes(s)) }
          : v,
      );
      const allGroupDone = verbs.every((v) => (v.pending_listings || []).length === 0);
      if (allGroupDone) {
        setTimeout(() => {
          setTaskGroups((prev) => prev.filter((g) => g.task_slug !== task_slug));
        }, 460);
      }
      return { ...prev, [task_slug]: verbs };
    });
    setRemovingRows((prev) => {
      const n = { ...prev };
      removedSlugs.forEach((s) => delete n[`${task_slug}-${verbiage_type}-${s}`]);
      return n;
    });
  };

  /* ─── Render: single verbiage section (inside group card) ──────────────── */
  const renderVerbiageSection = (group, verbiage, showDivider = false) => {
    const cardKey = `${group.task_slug}-${verbiage.type}`;
    const pmsInstructions = verbiage.pms_instructions || [];
    const [primaryPms, ...extraPms] = pmsInstructions;
    const pendingListings = verbiage.pending_listings || [];
    const isVerbiageDone = pendingListings.length === 0;
    const isCopied = copiedKey === cardKey;

    return (
      <div key={verbiage.type} className="task-verbiage">

        {/* Verbiage section heading + done tag */}
        {verbiage.verbiage_title && (
          <Col xs={24} sm={24} md={24} lg={16} xl={24} xxl={16} className="pl-0 pr-0">
            <div className="title-container">
              <Heading as="h3" className='mb-0'>
                <span className="text-uppercase">{verbiage.verbiage_title}</span>
              </Heading>
            </div>
            <Divider className="title-divider" />
          </Col>
        )}
        <div className="scheduler-row-inline">

          <div className="task-dir">
            <div className="task-dir-body">
              {primaryPms
                ? renderPmsBlock(primaryPms)
                : <strong className="scheduler-main-title color-dark">{verbiage.label}</strong>
              }
              {extraPms.map((pms) => (
                <Col key={pms.pms_type} xs={24} sm={24} md={24} lg={16} xl={24} xxl={16} className="pl-0 pr-0">
                  <div className="task-pms-extra pt-19">
                    {renderPmsBlock(pms)}
                  </div>
                </Col>
              ))}
            </div>
            <span className="task-done-tag">✓ Done</span>
          </div>
        </div>

        <Col xs={24} sm={24} md={24} lg={16} xl={24} xxl={16} className="pl-0 pr-0">
          {/* Copy box */}
          <div className="task-copybox mb-38">
            <div className="task-copybox-header">
              <span className="task-copybox-label">Content</span>
              <Button
                type="primary"
                outlined
                size="small"
                disabled={!verbiage.rendered_content}
                onClick={() => handleCopy(verbiage.rendered_content, cardKey)}
              >
                <CopyIcon />
                {isCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div
              className="task-text custom_editor_span_text"
              dangerouslySetInnerHTML={{ __html: verbiage.rendered_content || '<em>No content available.</em>' }}
            />
          </div>

          {/* Listing queue */}
          <div className="task-queue">
            <div className="task-q-head">
              <span className="task-q-head-label">Paste into each listing</span>
              <span className="task-q-count">{pendingListings.length} left</span>
              <Button
                type="primary"
                outlined
                size="small"
                disabled={isVerbiageDone}
                onClick={() => confirmMarkAllDone(group.task_slug, verbiage.type, pendingListings)}
              >
                Mark all done
              </Button>
            </div>
            <div className="task-q-list">
              {pendingListings.map((slug) => {
                const rowKey = `${group.task_slug}-${verbiage.type}-${slug}`;
                const isRemoving = !!removingRows[rowKey];
                return (
                  <div key={slug} className={`task-q-row${isRemoving ? ' removing' : ''}`}>
                    <span className="task-q-name">{listingNamesMap[slug] || slug}</span>
                    <Button
                      type="primary"
                      outlined
                      size="small"
                      disabled={isRemoving}
                      onClick={() => confirmMarkDone(group.task_slug, verbiage.type, slug)}
                    >
                      Done
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </Col>
        {showDivider && <Divider />}

      </div>
    );
  };

  /* ─── Render ───────────────────────────────────────────────────────────── */
  return (
    <div>
      <Spinner loader={loadingTasks || spinnerLoader} />

      {loadingTasks ? (
        <Row gutter={15} className="template-detail-root-row">
          <Col xs={24} md={24}>
            <div className="template-detail-loading-wrap">
              <Cards
                xs={24}
                headless
                className="borderRadius common_fiter_table template-detail-loading-card"
              >
                <Main className="mainBlock messages-setting-module">
                  <div className="template-detail-loading-space" />
                </Main>
              </Cards>
            </div>
          </Col>
        </Row>
      ) : (
        <>
          {taskGroups.length === 0 ? (

            <Row gutter={15} className="template-detail-root-row">
              <Col xs={24} md={24}>
                <div className="">
                  <div className="info">
                    <Cards xs={24} headless bodypadding={initialCardBodyPadding2} className="borderRadius common_fiter_table">
                      <Main className="mainBlock ">
                        <Row gutter={15}>
                          <Col xs={24} md={24}>
                            <div className="project-modal">
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={emptyState || 'No tasks scheduled.'}
                                style={{ marginTop: 60 }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Main>
                    </Cards>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <AddUser>
                <BasicFormWrapper>
                  <div headless className="host_userdetails_card">
                    <Row gutter={15} className="template-detail-root-row">
                      <Col xs={24} md={24}>
                        <div className="info">
                          <Cards xs={24} headless bodypadding={initialCardBodyPadding2} className="borderRadius common_fiter_table">
                            <Main className="mainBlock h-auto p-0">
                              {tabHeader && (
                                <Col xs={24} sm={24} md={24} lg={16} xl={24} xxl={16} className="pl-0 pr-0">
                                  <div
                                    className="info listing_detail_value"
                                    dangerouslySetInnerHTML={{ __html: tabHeader }}
                                  />
                                </Col>
                              )}
                            </Main>
                          </Cards>
                        </div>
                      </Col>
                    </Row>


                    {taskGroups.map((group, index) => {
                      const verbiages = verbiagesMap[group.task_slug] || [];
                      return (
                        <div key={group.task_slug} className="task-group-card mb-20">
                          <Cards
                            headless
                            bodypadding={initialCardBodyPadding}
                            className={`borderRadius-24 common_fiter_table${index === 0 ? ' mt-20' : ''}`}
                          >
                            {verbiages.map((verbiage, vIdx) => renderVerbiageSection(group, verbiage, vIdx < verbiages.length - 1))}
                          </Cards>
                        </div>
                      );
                    })}
                  </div>
                </BasicFormWrapper>
              </AddUser>
            </>
          )}
        </>
      )}
    </div>
  );

}

export default Tasks;
